/**
 * Set-membership classification of a point relative to a closed BRep solid.
 * Follows the Open CASCADE / Requicha pattern: IN / ON / OUT vs the opposing solid.
 * @module
 */
import { aabb3ContainsVec3, aabb3Copy, aabb3Grow } from "../aabb3";
import type { AABB3 } from "../aabb3Types";
import type { BRep, Face } from "../brep";
import { brepAABB3, classifyContainmentFaceVec2, computeBRepSignedVolumeFromFaceContributions } from "../brep";
import { computeConvexVertexInteriorPoint } from "../containment";
import { DIST_EPSILON } from "../core";
import { intersectFaceRay } from "../intersect";
import { planeEval, planeNormal, planeProject, planeSignedDistanceTo } from "../plane";
import type { Vec3 } from "../vec3";
import { vec3Dot, vec3Normalize } from "../vec3";

/**
 * Offset from a face into the source solid when testing INTERSECT membership.
 * Must be well below half the thinnest solids we intersect (half-story roof-clip gate
 * ribbons are TINY_PLANE_THICKNESS = 0.001"); 1e-3 landed on the opposite face (ON/OUT).
 */
const INWARD_SAMPLE_OFFSET = 1e-4;

const { abs } = Math;

export enum PointSolidState {
  IN = "IN",
  ON = "ON",
  OUT = "OUT",
}

/**
 * Unit directions: slightly perturbed axes so a ray is unlikely to lie in a face
 * plane or graze an edge shared by two faces (which would double-count crossings).
 * Pre-normalized so callers can use them without re-normalizing.
 */
const RAY_DIRECTIONS: ReadonlyArray<readonly [number, number, number]> = [
  [0.999997076650319, 0.0021339937615717807, 0.0011369966761514126],
  [0.0011369966761514126, 0.999997076650319, 0.0021339937615717807],
  [0.0021339937615717807, 0.0011369966761514126, 0.999997076650319],
];

/**
 * True when face plane normals point inward and must be flipped for outward samples.
 * Matches the signed-volume convention used by `normalizeBRepFaceNormalsOutward`
 * without mutating the BRep.
 */
export function brepNeedsOutwardNormalFlip(brep: BRep): boolean {
  const signedVolume = computeBRepSignedVolumeFromFaceContributions(brep);
  if (!Number.isFinite(signedVolume) || Math.abs(signedVolume) < 1e-6) {
    return false;
  }
  // Outward-oriented closed BReps produce a negative signed volume.
  return signedVolume > 0;
}

export interface FaceIntersectionClassifyContext {
  /** When true, negate each source face plane normal to get the outward direction. */
  sourceNeedsOutwardNormalFlip: boolean;
  /** Optional cached `brepAABB3(opposingBRep)` reused across many classify calls. */
  opposingAabb?: AABB3;
}

function pointOnBRepBoundary(x: number, y: number, z: number, brep: BRep, tol: number = DIST_EPSILON): boolean {
  for (const face of brep.faces) {
    if (!face) {
      continue;
    }
    if (abs(planeSignedDistanceTo(face.plane, x, y, z)) > tol) {
      continue;
    }
    const [u, v] = planeProject(face.plane, x, y, z);
    if (classifyContainmentFaceVec2(u, v, face)) {
      return true;
    }
  }
  return false;
}

/**
 * Counts transverse face crossings of a ray with t > tol.
 * Nearby hits (same t) are merged so edge-shared faces count once.
 * `dir` must be a unit vector (see RAY_DIRECTIONS).
 */
function countRayFaceCrossings(
  x: number,
  y: number,
  z: number,
  dirX: number,
  dirY: number,
  dirZ: number,
  brep: BRep,
  tol: number = DIST_EPSILON,
): number {
  const hitTs: number[] = [];
  for (const face of brep.faces) {
    if (!face) {
      continue;
    }
    const uv = intersectFaceRay(face, x, y, z, dirX, dirY, dirZ);
    if (!uv) {
      continue;
    }
    const [hx, hy, hz] = planeEval(face.plane, uv[0], uv[1]);
    const t = vec3Dot(hx - x, hy - y, hz - z, dirX, dirY, dirZ);
    if (t > tol) {
      hitTs.push(t);
    }
  }

  if (hitTs.length === 0) {
    return 0;
  }

  hitTs.sort((a, b) => a - b);
  let unique = 1;
  for (let i = 1; i < hitTs.length; i++) {
    if (hitTs[i] - hitTs[i - 1] > tol) {
      unique++;
    }
  }
  return unique;
}

/**
 * Classifies a point as IN / ON / OUT relative to a BRep solid.
 *
 * ON: within `tol` of some face and inside that face's loops.
 * IN/OUT: odd/even parity of ray–face crossings (OCCT-style Face/Solid membership).
 *
 * @param brepAabb Optional cached `brepAABB3(brep)` for repeated classifies against the same solid.
 */
export function classifyPointRelativeToBRep(
  x: number,
  y: number,
  z: number,
  brep: BRep,
  tol: number = DIST_EPSILON,
  brepAabb?: AABB3,
): PointSolidState {
  if (pointOnBRepBoundary(x, y, z, brep, tol)) {
    return PointSolidState.ON;
  }

  const aabb = aabb3Copy(brepAabb ?? brepAABB3(brep));
  aabb3Grow(aabb, tol, tol, tol);
  if (!aabb3ContainsVec3(aabb, x, y, z)) {
    return PointSolidState.OUT;
  }

  // A single ray can get the wrong parity if it grazes a shared edge or vertex.
  // Cast perturbed directions and take the majority vote; stop once two agree.
  let inVotes = 0;
  let outVotes = 0;
  for (const [dx, dy, dz] of RAY_DIRECTIONS) {
    const crossings = countRayFaceCrossings(x, y, z, dx, dy, dz, brep, tol);
    if (crossings % 2 === 1) {
      inVotes++;
    } else {
      outVotes++;
    }
    if (inVotes === 2 || outVotes === 2) {
      break;
    }
  }

  return inVotes >= outVotes ? PointSolidState.IN : PointSolidState.OUT;
}

/**
 * Sample an interior 3D point of a face for solid-membership tests.
 *
 * Uses only the outer loop (`face.loops[0]`). Safe for DCEL intersection fragments
 * (no holes). Do not reuse for faces with inner hole loops without also testing
 * hole containment — an ear centroid can land inside a hole.
 */
function sampleFaceInteriorPoint(face: Face): Vec3 {
  const [u, v] = computeConvexVertexInteriorPoint(face.loops[0]);
  const [x, y, z] = planeEval(face.plane, u, v);
  return [x, y, z];
}

function outwardUnitNormalForFace(face: Face, sourceNeedsOutwardNormalFlip: boolean): Vec3 {
  let [nx, ny, nz] = planeNormal(face.plane);
  [nx, ny, nz] = vec3Normalize(nx, ny, nz);
  if (sourceNeedsOutwardNormalFlip) {
    return [-nx, -ny, -nz];
  }
  return [nx, ny, nz];
}

/**
 * INTERSECT / COMMON retention test for a face fragment of a source solid.
 *
 * Uses a point just inside the source solid (regularized membership): keep only when
 * that point is IN the opposing solid. Pure ON contact without volume overlap is rejected
 * (e.g. stacked cubes that only share a face).
 *
 * Callers should compute `sourceNeedsOutwardNormalFlip` / `opposingAabb` once per operand
 * (see `brepNeedsOutwardNormalFlip` / `brepAABB3`) rather than per fragment.
 */
export function faceFragmentBelongsInIntersection(
  face: Face,
  opposingBRep: BRep,
  context: FaceIntersectionClassifyContext,
): boolean {
  const [x, y, z] = sampleFaceInteriorPoint(face);
  const [ox, oy, oz] = outwardUnitNormalForFace(face, context.sourceNeedsOutwardNormalFlip);
  const ix = x - INWARD_SAMPLE_OFFSET * ox;
  const iy = y - INWARD_SAMPLE_OFFSET * oy;
  const iz = z - INWARD_SAMPLE_OFFSET * oz;
  return (
    classifyPointRelativeToBRep(ix, iy, iz, opposingBRep, DIST_EPSILON, context.opposingAabb) === PointSolidState.IN
  );
}
